import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  requestDataRoomLaunch,
  submitDataRoomHandoff,
  type DataRoomLaunchResponse,
  type RefusalReason,
} from '../api/data-room.api';

export interface DataRoomLaunchState {
  /** Asks the backend to start a handoff, and performs it if it says yes. */
  launch: () => void;
  isLaunching: boolean;
  /** The backend's typed reason, or `null` while there is nothing to refuse. */
  refusal: RefusalReason | null;
}

/**
 * Drives one data-room handoff (MYN-164). The launch is an authenticated
 * `POST` that records the attempt and answers with a typed decision; on yes it
 * returns a handoff URL and a one-time ticket, which are submitted as a form
 * POST. The response to that is the server-rendered form that posts to DAXE,
 * so the DAXE token is minted server-side and never reaches this app.
 *
 * Two behaviours are deliberate:
 *
 * 1. **It never hands off from a hidden tab.** The handoff ticket lives 60
 *    seconds and is single-use, so a tab the browser has backgrounded can sit
 *    long enough to submit a dead one - the expired/replayed case. That is a
 *    mechanism failure rather than a user error, and any user who switches
 *    tabs will hit it, so instead of an error state, returning to the tab
 *    relaunches and mints a fresh ticket. The stale ticket is simply dropped;
 *    it is never submitted, retried, or stored.
 * 2. **A launch call that never completes is reported as
 *    `upstream_unavailable`.** To the user that is precisely what happened -
 *    the data room could not be reached - and it is the one reason that offers
 *    a retry rather than a dead end. A raw transport error is never surfaced
 *    (`docs/specs/features/data-room/02-design.md` §12).
 *
 * The handoff runs from the mutation's own `onSuccess`, not from an effect
 * watching its result: a launch that resolves twice must hand off twice, and
 * result identity cannot be relied on for that. It also means the ticket is
 * read once, at the moment it is submitted, and is never held in React state.
 *
 * @param startupId - the startup entity whose data room is being opened
 */
export function useDataRoomLaunch(startupId: string): DataRoomLaunchState {
  // The reissue waits on a DOM event rather than on React state, so the
  // listener is tracked here and detached on unmount and on every new launch.
  const reissueListener = useRef<(() => void) | null>(null);

  const stopWaitingForTab = useCallback(() => {
    if (!reissueListener.current) return;
    document.removeEventListener('visibilitychange', reissueListener.current);
    reissueListener.current = null;
  }, []);

  const { mutate, data, isPending, isError } = useMutation<DataRoomLaunchResponse, Error, void>({
    mutationFn: () => requestDataRoomLaunch(startupId),
    onSuccess: (decision) => {
      if (!decision.entitled) return;

      if (!document.hidden) {
        submitDataRoomHandoff(decision);
        return;
      }

      const reissueWhenVisible = () => {
        if (document.hidden) return;
        stopWaitingForTab();
        mutate();
      };
      reissueListener.current = reissueWhenVisible;
      document.addEventListener('visibilitychange', reissueWhenVisible);
    },
  });

  const launch = useCallback(() => {
    stopWaitingForTab();
    mutate();
  }, [stopWaitingForTab, mutate]);

  useEffect(() => stopWaitingForTab, [stopWaitingForTab]);

  const refusal: RefusalReason | null = isError
    ? 'upstream_unavailable'
    : data && !data.entitled
      ? data.reason
      : null;

  return { launch, isLaunching: isPending, refusal };
}
